import React, { useState } from "react";
import DashboardLayout from "@/components/DashboardLayout";
import { api } from "@/lib/api";

const MODES = [
  { v: "sea_fcl", l: "Sea — Full Container Load (20ft)" },
  { v: "sea_lcl", l: "Sea — Less than Container Load (per pallet)" },
  { v: "air",     l: "Air freight" },
  { v: "rail",    l: "Rail (40ft container)" },
];

export default function Logistics() {
  const [pallets, setPallets] = useState(40);
  const [port, setPort] = useState("Rotterdam");
  const [mode, setMode] = useState("sea_lcl");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true); setResult(null);
    try {
      const { data } = await api.post("/logistics/calculate", {
        pallets: Number(pallets), destination_port: port, freight_mode: mode,
      });
      setResult(data);
    } catch (e) {
      setError(e.response?.data?.detail || "Calculation failed");
    } finally { setLoading(false); }
  };

  return (
    <DashboardLayout testid="logistics-page">
      <div className="p-8 md:p-12 max-w-6xl">
        <div className="overline">Bulk Order &amp; Logistics Integration</div>
        <h1 className="h-serif text-5xl text-ink mt-3">Freight &amp; Customs Calculator</h1>
        <p className="text-sub mt-4 max-w-2xl">
          Calculate freight options, automatic multi-pallet discounts, and
          estimated customs clearance time at destination — before the order
          leaves the cart.
        </p>

        <div className="mt-10 grid md:grid-cols-12 gap-10">
          <form onSubmit={submit} className="md:col-span-5 space-y-7" data-testid="logistics-form">
            <Field label="Pallets / Units">
              <input type="number" min="1" className="input-line tabular" value={pallets}
                     onChange={(e) => setPallets(e.target.value)} data-testid="lg-pallets" />
            </Field>
            <Field label="Destination port">
              <input className="input-line" value={port} onChange={(e) => setPort(e.target.value)}
                     placeholder="Rotterdam · Singapore · Houston · Santos…" data-testid="lg-port" />
            </Field>
            <Field label="Freight mode">
              <select className="input-line" value={mode} onChange={(e) => setMode(e.target.value)} data-testid="lg-mode">
                {MODES.map((m) => <option key={m.v} value={m.v}>{m.l}</option>)}
              </select>
            </Field>
            <button className="btn-primary" disabled={loading} data-testid="lg-submit">
              {loading ? "Calculating…" : "Calculate →"}
            </button>
            {error && <div className="border-l-4 border-danger bg-parchment p-4 text-danger" data-testid="lg-error">{error}</div>}
          </form>

          <div className="md:col-span-7 bg-parchment border border-line p-8 min-h-[300px]" data-testid="lg-results">
            <div className="overline">Order summary — indicative</div>
            {!result && (
              <div className="text-sub mt-6">Enter parameters to see freight, discount, and customs estimate.</div>
            )}
            {result && (
              <>
                <h3 className="h-serif text-3xl text-ink mt-2">{result.unit_label}</h3>
                <div className="mt-6 divide-y divide-line border-y border-line">
                  <Row k="Gross freight" v={`$${result.gross_freight_usd.toLocaleString()}`} />
                  <Row k={`Multi-pallet discount (${Math.round(result.discount_pct * 100)}%)`}
                       v={`− $${result.discount_usd.toLocaleString()}`} />
                  <Row k="Net freight" v={`$${result.net_freight_usd.toLocaleString()}`} highlight />
                  <Row k="Destination port" v={result.destination_port} />
                  <Row k="Estimated customs clearance" v={`${result.estimated_customs_clearance_days} business days`} />
                </div>
                <p className="text-xs text-sub mt-6 font-mono uppercase tracking-wider">
                  Indicative only — binding rates confirmed via RFQ
                </p>
              </>
            )}
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="overline block mb-2">{label}</span>
      {children}
    </label>
  );
}
function Row({ k, v, highlight }) {
  return (
    <div className="py-4 flex items-baseline justify-between">
      <span className={`text-sm ${highlight ? "text-ink" : "text-sub"}`}>{k}</span>
      <span className={`tabular ${highlight ? "h-serif text-2xl text-oxblood" : "font-mono text-sm text-ink"}`}>{v}</span>
    </div>
  );
}
